import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../styles/OutputViewer.css';

import { useAuth } from '../components/AuthContext';
const API_BASE = import.meta.env.VITE_API_BASE;

/* ── tiny SVG helpers ── */
const SvgSearch = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,opacity:.35}}>
    <circle cx="5.5" cy="5.5" r="3.8" stroke="currentColor" strokeWidth="1.3"/>
    <path d="M9 9l2.5 2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);
const SvgDownload = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2 8v2h8V8M6 2v5M3.5 5L6 7.5 8.5 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const SvgFilter = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
    <path d="M1 3h9M3 5.5h5M5 8h1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);
const SvgChevL = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M6.5 2L3.5 5l3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
);
const SvgChevR = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M3.5 2L6.5 5l-3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
);
const SvgExport = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M2 9v2h9V9M6.5 2v6M4 6l2.5 2.5L9 6" stroke="#9ca3af" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const SvgReload = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M6 3.5v2.5l1.5 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

const DOT_COLORS = ['#6366f1','#0f766e','#b45309','#475569','#9ca3af','#e11d48','#0284c7','#7c3aed'];
const ROWS_PER_PAGE = 10;

function OutputViewer({ jobId, onNavigate }) {
  const { apiFetch, authHeaders } = useAuth();
  const [searchParams] = useSearchParams();
  const folderFromUrl = searchParams.get('folder');
  /* ── state ── */
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  // table state
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleCols, setVisibleCols] = useState({});
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [validationLoading, setValidationLoading] = useState(false);
  const [revalidating, setRevalidating] = useState(false);
  const [hasRuleConfig, setHasRuleConfig] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const validationSectionRef = useRef(null);
  const [expandedValidationIndex, setExpandedValidationIndex] = useState(null);
  const [validationHistory, setValidationHistory] = useState([]);
  const [expandedValidationDetailIndex, setExpandedValidationDetailIndex] = useState(null);

  /* ── data loading (unchanged logic) ── */
  useEffect(() => { loadFolders(); }, []);

  // Reset expanded validation when history changes
  useEffect(() => {
    console.log('[Effect] validationHistory changed:', validationHistory.length, 'runs');
    setExpandedValidationIndex(null);
  }, [validationHistory]);

  const loadFolders = async () => {
    try {
      setLoading(true);
      const resp = await apiFetch(`${API_BASE}/outputs/browse`);
      if (resp.ok) {
        const data = await resp.json();
        setFolders(data.folders || []);
        if (data.folders && data.folders.length > 0) {
          // Use folder from URL query param if provided, otherwise first
          const target = folderFromUrl && data.folders.some(f => f.folder === folderFromUrl)
            ? folderFromUrl
            : data.folders[0].folder;
          setSelectedFolder(target);
          await loadFilesForFolder(target);
        }
      }
    } catch (err) { console.error('Error fetching folders:', err); }
    finally { setLoading(false); }
  };

  const loadFilesForFolder = async (folder) => {
    try {
      const r = await apiFetch(`${API_BASE}/outputs/browse/${folder}`);
      if (r.ok) { const d = await r.json(); setFiles(d.files || []); }
    } catch (err) { console.error('Error fetching folder files:', err); }

    // Load validation data for this folder
    console.log(`[LoadFolder] About to load validation data`);
    setValidationLoading(true);
    console.log(`[LoadFolder] Set validationLoading to true`);
    try {
      const url = `${API_BASE}/outputs/validation/${encodeURIComponent(folder)}`;
      console.log(`[LoadFolder] Fetching from: ${url}`);
      const vr = await apiFetch(url);
      console.log(`[LoadFolder] Response status: ${vr.status}`);
      if (vr.ok) {
        const fullData = await vr.json();
        console.log('[LoadFolder] Full validation data received:', fullData);
        
        // Handle both old format and new history format
        if (fullData.history) {
          // New format with history
          console.log('[LoadFolder] Using history format with', fullData.history.length, 'validation runs');
          setValidationHistory(fullData.history || []);
          setExpandedValidationIndex(null); // Reset expansion
        } else {
          // Old format - treat as single validation result
          console.log('[LoadFolder] Using old format (no history)');
          setValidationHistory(fullData ? [fullData] : []);
          setExpandedValidationIndex(null);
        }
        
      } else {
        console.warn(`[LoadFolder] Validation response not ok: ${vr.status}`);
        setValidationHistory([]);
      }
    } catch (err) {
      console.error('[LoadFolder] Error fetching validation:', err);
      setValidationHistory([]);
    }

    // Check for rule_config in metadata
    try {
      const mr = await apiFetch(`${API_BASE}/outputs/metadata/${encodeURIComponent(folder)}`);
      if (mr.ok) {
        const mdata = await mr.json();
        setHasRuleConfig(mdata.has_rule_config || false);
        setMetadata(mdata.metadata || null);
      } else {
        setHasRuleConfig(false);
        setMetadata(null);
      }
    } catch (err) {
      console.error('Error fetching metadata:', err);
      setHasRuleConfig(false);
      setMetadata(null);
    }
    setValidationLoading(false);
  };

  const revalidateFolder = async () => {
    if (!selectedFolder) return;
    console.log(`[Revalidate] Starting revalidation for folder: ${selectedFolder}`);
    setRevalidating(true);
    try {
      const resp = await apiFetch(`${API_BASE}/outputs/revalidate/${encodeURIComponent(selectedFolder)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      console.log('[Revalidate] Response status:', resp.status);
      if (resp.ok) {
        const fullData = await resp.json();
        console.log('[Revalidate] Response data received:', fullData);
        
        // Update validation history with new result
        if (fullData.history) {
          console.log('[Revalidate] Updating history with', fullData.history.length, 'runs');
          setValidationHistory(fullData.history);
          setExpandedValidationIndex(null); // Reset expansion after revalidation
        }
        
        // Check for debug errors
        if (fullData._debug_error) {
          console.error('[Revalidate] Backend error:', fullData._debug_error);
          alert(`Revalidation Error: ${fullData._debug_error}`);
        }
        
        console.log('[Revalidate] Validation history updated');
        // Scroll validation section into view after a short delay
        setTimeout(() => {
          if (validationSectionRef.current) {
            validationSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            console.log('[Revalidate] Scrolled validation section into view');
          }
        }, 100);
      } else {
        console.error('Revalidation failed:', resp.status, resp.statusText);
        alert(`Revalidation failed with status ${resp.status}`);
      }
    } catch (err) {
      console.error('Error revalidating:', err);
      alert(`Error during revalidation: ${err.message}`);
    }
    setRevalidating(false);
  };


  const selectFolder = async (folder) => {
    setSelectedFolder(folder);
    setSelectedFile(null);
    setPreview(null);
    setSearchQuery('');
    setCurrentPage(1);
    setSelectedRows(new Set());
    setLoading(true);
    await loadFilesForFolder(folder);
    setLoading(false);
  };

  // Delete a dataset (output folder)
  const deleteFolder = async (folder) => {
    if (!window.confirm(`Are you sure you want to delete dataset "${folder}"? This cannot be undone.`)) return;
    try {
      const resp = await apiFetch(`${API_BASE}/outputs/delete/${encodeURIComponent(folder)}`, { method: 'DELETE' });
      if (resp.status === 204) {
        // If the deleted folder was selected, clear selection
        if (selectedFolder === folder) {
          setSelectedFolder(null);
          setFiles([]);
          setPreview(null);
        }
        await loadFolders();
      } else {
        alert(`Failed to delete dataset: ${resp.status}`);
      }
    } catch (err) {
      alert(`Error deleting dataset: ${err.message}`);
    }
  };

  const previewFile = async (filename) => {
    if (!selectedFolder && !jobId) return;
    setLoading(true);
    setPreview(null);
    try {
      const endpoint = jobId
        ? `${API_BASE}/outputs/${jobId}/${filename}/preview?lines=500`
        : `${API_BASE}/browse/${selectedFolder}/${filename}/preview?lines=500`;
      const response = await apiFetch(endpoint);
      if (response.ok) {
        const data = await response.json();
        if (data.data && data.data.length > 0 && filename.endsWith('.csv')) {
          const lines = data.data;
          const headers = lines[0].split(',').map(h => h.trim());
          const rows = [];
          for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim()) rows.push(lines[i].split(',').map(c => c.trim()));
          }
          data.table = { headers, rows };
          // init visible columns
          const vis = {};
          headers.forEach(h => { vis[h] = true; });
          setVisibleCols(vis);
        }
        setPreview(data);
        setSelectedFile(filename);
        setCurrentPage(1);
        setSelectedRows(new Set());
        setSearchQuery('');
        setSortCol(null);
      }
    } catch (error) { console.error('Error previewing file:', error); }
    setLoading(false);
  };

  const downloadFile = async (filename) => {
    const endpoint = jobId
      ? `${API_BASE}/outputs/${jobId}/${filename}`
      : `${API_BASE}/browse/${selectedFolder}/${filename}`;
    try {
      const resp = await apiFetch(endpoint);
      if (resp.ok) {
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      }
    } catch { window.location.href = endpoint; }
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  /* ── derived data ── */
  const currentFolderInfo = folders.find(f => f.folder === selectedFolder);
  // Prioritize: API folder data → metadata object → estimate
  // metadata is loaded via loadFilesForFolder, so it should always have total_records if available
  const totalRows = currentFolderInfo?.total_records ?? metadata?.total_records ?? (folders.find(f => f.folder === selectedFolder)?.file_count || 0) * 500;

  /* Derive a human-readable config name from the folder name (dataset name) */
  const configName = useMemo(() => {
    if (!selectedFolder) return null;
    // If it looks like a timestamp-only folder (YYYYMMDD_HHMMSS), derive from file names
    if (/^\d{8}_\d{6}$/.test(selectedFolder)) {
      if (!files || files.length === 0) return selectedFolder;
      const names = files
        .map(f => f.filename.replace(/\.[^.]+$/, ''))
        .map(n => n.replace(/[_-]/g, ' '))
        .map(n => n.charAt(0).toUpperCase() + n.slice(1));
      if (names.length <= 3) return names.join(', ');
      return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
    }
    // Otherwise the folder IS the dataset name
    return selectedFolder;
  }, [selectedFolder, files]);

  const headers = preview?.table?.headers || [];
  const activeHeaders = headers.filter(h => visibleCols[h]);

  // filter + sort rows
  const processedRows = useMemo(() => {
    if (!preview?.table?.rows) return [];
    let rows = [...preview.table.rows];
    // search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(r => r.some(cell => cell.toLowerCase().includes(q)));
    }
    // sort
    if (sortCol !== null) {
      const idx = headers.indexOf(sortCol);
      if (idx >= 0) {
        rows.sort((a, b) => {
          const av = a[idx] || '', bv = b[idx] || '';
          const an = Number(av), bn = Number(bv);
          if (!isNaN(an) && !isNaN(bn)) return sortDir === 'asc' ? an - bn : bn - an;
          return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        });
      }
    }
    return rows;
  }, [preview, searchQuery, sortCol, sortDir, headers]);

  const totalPages = Math.max(1, Math.ceil(processedRows.length / ROWS_PER_PAGE));
  const pageRows = processedRows.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);

  const toggleSort = (col) => {
    if (sortCol === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortCol(col); setSortDir('asc'); }
  };

  const toggleCol = (h) => setVisibleCols(v => ({ ...v, [h]: !v[h] }));

  const toggleRow = (idx) => {
    setSelectedRows(prev => {
      const s = new Set(prev);
      s.has(idx) ? s.delete(idx) : s.add(idx);
      return s;
    });
  };

  const toggleAllPage = () => {
    const globalStart = (currentPage - 1) * ROWS_PER_PAGE;
    const allSelected = pageRows.every((_, i) => selectedRows.has(globalStart + i));
    setSelectedRows(prev => {
      const s = new Set(prev);
      pageRows.forEach((_, i) => { allSelected ? s.delete(globalStart + i) : s.add(globalStart + i); });
      return s;
    });
  };

  const formatDate = (dateStr) => {
    try { return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return dateStr; }
  };

  const isBoolCol = (colIdx) => {
    if (!preview?.table?.rows || preview.table.rows.length === 0) return false;
    const vals = preview.table.rows.slice(0, 20).map(r => (r[colIdx] || '').toLowerCase());
    return vals.every(v => v === 'true' || v === 'false');
  };

  /* detect which column looks like a "name" field */
  const nameColIdx = headers.findIndex(h => /name/i.test(h));

  /* ── pager helper ── */
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('…');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('…');
      pages.push(totalPages);
    }
    return pages;
  };

  /* ── render ── */
  return (
    <div className="ov-layout">
      {/* ── Sidebar ── */}
      <aside className="ov-sidebar">
        <div className="sb-section">Datasets</div>
        {folders.map((f, i) => (
          <div
            key={f.folder}
            className={`sb-item ${selectedFolder === f.folder ? 'active' : ''}`}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', flex: 1, cursor: 'pointer' }} onClick={() => selectFolder(f.folder)}>
              <div className="sb-dot" style={{ background: DOT_COLORS[i % DOT_COLORS.length] }} />
              <div className="sb-name-block">
                <span className="sb-name">{f.folder}</span>
                <span className="sb-name-hint">{f.file_count} file{f.file_count !== 1 ? 's' : ''}</span>
              </div>
              <span className="sb-count">{(f.total_records || f.file_count * 500).toLocaleString()}</span>
            </div>
            <button
              className="ov-btn ov-btn-xs ov-btn-danger"
              style={{ marginLeft: 4, padding: '2px 6px', fontSize: 11 }}
              title="Delete dataset"
              onClick={e => { e.stopPropagation(); deleteFolder(f.folder); }}
            >
              Delete
            </button>
          </div>
        ))}

        {files.length > 0 && (
          <>
            <div className="sb-section" style={{ marginTop: 12 }}>Files</div>
            {files.map((file, i) => (
              <div
                key={i}
                className={`sb-item ${selectedFile === file.filename ? 'active' : ''}`}
                onClick={() => previewFile(file.filename)}
              >
                <SvgExport />
                <span className="sb-name">{file.filename}</span>
              </div>
            ))}
          </>
        )}

        <div className="sb-footer">
          {folders.length} datasets · {totalRows.toLocaleString()} rows total
        </div>
      </aside>

      {/* ── Main area ── */}
      <div className="ov-main">
        {/* Page header */}
        <div className="ov-page-header">
          <div>
            <div className="ov-breadcrumb">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 5h8M5 2l3 3-3 3" stroke="#9ca3af" strokeWidth="1.2" strokeLinecap="round"/></svg>
              Datasets ›&nbsp;<span>{selectedFolder || '—'}</span>
              {selectedFile && (<>&nbsp;›&nbsp;<span>{selectedFile}</span></>)}
            </div>
            <div className="ov-page-title">{selectedFile || configName || selectedFolder || 'Data Viewer'}</div>
            <div className="ov-page-sub">
              {currentFolderInfo && (
                <>{selectedFolder} · Generated {formatDate(currentFolderInfo.timestamp)} · {currentFolderInfo.file_count} file{currentFolderInfo.file_count !== 1 ? 's' : ''} · {totalRows.toLocaleString()} rows</>
              )}
            </div>
          </div>
          <div className="ov-hdr-actions">
            <button className="ov-btn ov-btn-sm" onClick={loadFolders}>
              <SvgReload /> Refresh
            </button>
            <button 
              className="ov-btn ov-btn-sm" 
              onClick={revalidateFolder} 
              disabled={!selectedFolder || revalidating}
              title={!hasRuleConfig ? "No rule configuration found — revalidation may not run checks" : ""}
            >
              <SvgReload /> {revalidating ? 'Revalidating…' : 'Revalidate'}
            </button>
            {selectedFile && (
              <button className="ov-btn ov-btn-sm" onClick={() => downloadFile(selectedFile)}>
                <SvgDownload /> Export CSV
              </button>
            )}
            <button className="ov-btn ov-btn-primary ov-btn-sm" onClick={() => onNavigate('rules')}>
              + New dataset
            </button>
          </div>
        </div>

        {/* Stat strip */}
        <div className="ov-stat-strip">
          <div className="ov-stat-card">
            <div className="ov-stat-label">Total rows</div>
            <div className="ov-stat-value">{preview?.table ? (preview.total_lines ? preview.total_lines.toLocaleString() : processedRows.length.toLocaleString()) : totalRows ? totalRows.toLocaleString() : '—'}</div>
            <div className="ov-stat-bar"><div className="ov-stat-bar-fill" style={{ width: '100%', background: '#6366f1' }} /></div>
          </div>
          <div className="ov-stat-card">
            <div className="ov-stat-label">Fields</div>
            <div className="ov-stat-value">{headers.length || '—'}</div>
            <div className="ov-stat-sub">{headers.slice(0, 5).join(' · ')}{headers.length > 5 ? ' …' : ''}</div>
          </div>
          <div className="ov-stat-card">
            <div className="ov-stat-label">Validation</div>
            <div className="ov-stat-value" style={{ color: '#166534' }}>{preview?.table ? 'Passed' : '—'}</div>
            <div className="ov-stat-sub">0 issues · checked on generate</div>
          </div>
          <div className="ov-stat-card">
            <div className="ov-stat-label">Config</div>
            <div className="ov-stat-value" style={{ fontSize: 15, marginTop: 5 }}>{configName || '—'}</div>
            <div className="ov-stat-sub">{selectedFolder ? `${selectedFolder}${selectedFile ? ' › ' + selectedFile : ''}` : 'select a dataset'}</div>
          </div>
        </div>

        {/* Table card */}
        {preview?.table ? (
          <div className="ov-table-card">
            {/* Column selector */}
            <div className="ov-col-selector">
              <span className="ov-col-label">Columns</span>
              <span className={`ov-col-tog ${visibleCols['#'] !== false ? 'on' : ''}`} onClick={() => toggleCol('#')}>#</span>
              {headers.map(h => (
                <span key={h} className={`ov-col-tog ${visibleCols[h] ? 'on' : ''}`} onClick={() => toggleCol(h)}>{h}</span>
              ))}
            </div>

            {/* Toolbar */}
            <div className="ov-toolbar">
              <div className="ov-search-wrap">
                <SvgSearch />
                <input
                  placeholder="Search rows…"
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                />
              </div>
              <div className="ov-filter-pill">
                <SvgFilter /> Add filter
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <span className="ov-filter-pill" style={{ fontSize: '11px', color: '#9ca3af' }}>
                  {processedRows.length} row{processedRows.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {/* Table */}
            <div className="ov-table-wrap">
              <table className="ov-table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>
                      <input type="checkbox" onChange={toggleAllPage}
                        checked={pageRows.length > 0 && pageRows.every((_, i) => selectedRows.has((currentPage - 1) * ROWS_PER_PAGE + i))}
                        style={{ width: 13, height: 13, accentColor: '#6366f1' }}
                      />
                    </th>
                    {visibleCols['#'] !== false && (
                      <th className={sortCol === '#' ? 'sorted' : ''} onClick={() => toggleSort('#')}>#</th>
                    )}
                    {headers.map((h, hi) => visibleCols[h] && (
                      <th key={h} className={sortCol === h ? 'sorted' : ''} onClick={() => toggleSort(h)}>
                        {h}
                        {sortCol === h && (
                          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ display: 'inline', verticalAlign: 'middle', marginLeft: 4 }}>
                            {sortDir === 'asc'
                              ? <path d="M4 7V1M1.5 3.5L4 1l2.5 2.5" stroke="#6366f1" strokeWidth="1.2" strokeLinecap="round"/>
                              : <path d="M4 1v6M1.5 4.5L4 7l2.5-2.5" stroke="#6366f1" strokeWidth="1.2" strokeLinecap="round"/>
                            }
                          </svg>
                        )}
                      </th>
                    ))}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 ? (
                    <tr className="ov-empty-row"><td colSpan={activeHeaders.length + 3}>No matching rows</td></tr>
                  ) : pageRows.map((row, ri) => {
                    const globalIdx = (currentPage - 1) * ROWS_PER_PAGE + ri;
                    return (
                      <tr key={globalIdx}>
                        <td>
                          <input type="checkbox" checked={selectedRows.has(globalIdx)} onChange={() => toggleRow(globalIdx)}
                            style={{ width: 13, height: 13, accentColor: '#6366f1' }}
                          />
                        </td>
                        {visibleCols['#'] !== false && <td className="col-id">{globalIdx + 1}</td>}
                        {row.map((cell, ci) => {
                          if (!visibleCols[headers[ci]]) return null;
                          const isBool = isBoolCol(ci);
                          const isName = ci === nameColIdx;
                          return (
                            <td key={ci} className={isName ? 'col-name' : 'mono'}>
                              {isBool
                                ? <span className={`ov-chip ${cell.toLowerCase() === 'true' ? 'ov-chip-green' : 'ov-chip-red'}`}>{cell}</span>
                                : cell
                              }
                            </td>
                          );
                        })}
                        <td>
                          <div className="col-action">
                            <div className="ov-icon-btn" title="View" onClick={() => {}}>
                              <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M1 5.5h9M5.5 1l4.5 4.5L5.5 10" stroke="#6b7280" strokeWidth="1.2" strokeLinecap="round"/></svg>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="ov-table-footer">
              <div className="ov-footer-left">
                {selectedRows.size > 0 && (
                  <>
                    <span>{selectedRows.size} selected</span>
                    <button className="ov-btn ov-btn-sm" style={{ fontSize: '10.5px' }}>Export selected</button>
                    <span style={{ color: '#d1d5db' }}>|</span>
                  </>
                )}
                <span>Showing {Math.min((currentPage - 1) * ROWS_PER_PAGE + 1, processedRows.length)}–{Math.min(currentPage * ROWS_PER_PAGE, processedRows.length)} of {processedRows.length} rows</span>
              </div>
              <div className="ov-pager">
                <div className={`ov-page-btn ${currentPage <= 1 ? 'disabled' : ''}`}
                  onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}>
                  <SvgChevL />
                </div>
                {getPageNumbers().map((p, i) =>
                  p === '…'
                    ? <div key={'e' + i} className="ov-page-btn ov-page-ellipsis">…</div>
                    : <div key={p} className={`ov-page-btn ${currentPage === p ? 'active' : ''}`}
                        onClick={() => setCurrentPage(p)}>{p}</div>
                )}
                <div className={`ov-page-btn ${currentPage >= totalPages ? 'disabled' : ''}`}
                  onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}>
                  <SvgChevR />
                </div>
              </div>
            </div>
          </div>
        ) : !selectedFile && files.length > 0 ? (
          /* File grid – shown when a dataset is selected but no file is open */
          <div className="ov-file-grid">
            {files.map((file, i) => {
              const ext = file.filename.split('.').pop().toLowerCase();
              const isCSV = ext === 'csv';
              const isJSON = ext === 'json';
              return (
                <div key={i} className="ov-file-tile" onClick={() => previewFile(file.filename)}>
                  <div className="ov-file-tile-icon">
                    {isCSV ? (
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <rect x="3" y="2" width="22" height="24" rx="3" stroke="#6366f1" strokeWidth="1.3" fill="#eef2ff"/>
                        <path d="M8 10h12M8 14h12M8 18h8" stroke="#6366f1" strokeWidth="1.1" strokeLinecap="round"/>
                      </svg>
                    ) : isJSON ? (
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <rect x="3" y="2" width="22" height="24" rx="3" stroke="#b45309" strokeWidth="1.3" fill="#fef3c7"/>
                        <path d="M10 10l-2 4 2 4M18 10l2 4-2 4M13 9l2 10" stroke="#b45309" strokeWidth="1.1" strokeLinecap="round"/>
                      </svg>
                    ) : (
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <rect x="3" y="2" width="22" height="24" rx="3" stroke="#6b7280" strokeWidth="1.3" fill="#f3f4f6"/>
                        <path d="M8 10h12M8 14h12M8 18h6" stroke="#6b7280" strokeWidth="1.1" strokeLinecap="round"/>
                      </svg>
                    )}
                  </div>
                  <div className="ov-file-tile-name">{file.filename}</div>
                  <div className="ov-file-tile-meta">
                    <span>{formatFileSize(file.size_bytes)}</span>
                    <span className="ov-file-tile-ext">{ext.toUpperCase()}</span>
                  </div>
                  <div className="ov-file-tile-actions">
                    <button className="ov-btn ov-btn-sm" onClick={(e) => { e.stopPropagation(); previewFile(file.filename); }}>
                      Preview
                    </button>
                    <button className="ov-btn ov-btn-sm" onClick={(e) => { e.stopPropagation(); downloadFile(file.filename); }}>
                      <SvgDownload /> Download
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Non-CSV preview or truly empty */
          <div className="ov-table-card" style={{ padding: 0 }}>
            {!selectedFile && files.length === 0 ? (
              <div className="ov-empty-state">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none" style={{marginBottom:10,opacity:.4}}>
                  <rect x="4" y="3" width="28" height="30" rx="4" stroke="currentColor" strokeWidth="1.3"/>
                  <path d="M11 13h14M11 18h14M11 23h9" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
                </svg>
                <p>Select a dataset from the sidebar to view files.</p>
              </div>
            ) : loading ? (
              <div className="ov-empty-state"><p>Loading…</p></div>
            ) : preview && preview.data ? (
              <div className="ov-raw-preview">
                {preview.data.map((line, idx) => (
                  <div key={idx} className="ov-raw-line">
                    <span className="ov-raw-num">{idx + 1}</span>
                    <span className="ov-raw-text">{line}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ov-empty-state"><p>No preview data available.</p></div>
            )}
          </div>
        )}

        {/* Validation Results Section */}
        {selectedFolder && (
          <div className="ov-validation-section" ref={validationSectionRef}>
            <div className="ov-validation-header">
              <h3>Validation History</h3>
              {(validationLoading || revalidating) && <span className="ov-loading-text">Loading…</span>}
              {validationHistory.length > 0 && (
                <span className="ov-validation-timestamp">
                  {validationHistory.length} validation{validationHistory.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            {validationHistory.length > 0 ? (
              <table className="ov-validation-table">
                <thead>
                  <tr>
                    <th>Date & Time</th>
                    <th>Status</th>
                    <th>Checks</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {validationHistory.slice().reverse().map((validation, index) => {
                    const actualIndex = validationHistory.length - 1 - index;
                    const isExpanded = expandedValidationDetailIndex === actualIndex;
                    const checkCount = Object.keys(validation.checks || {}).length;
                    
                    return (
                      <React.Fragment key={actualIndex}>
                        <tr className={actualIndex % 2 === 0 ? 'row-even' : ''}>
                          <td>
                            <span className="ov-datetime">
                              {new Date(validation.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              {' · '}
                              {new Date(validation.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </td>
                          <td>
                            <span className={`ov-status-badge ${validation.valid ? 'pass' : 'fail'}`}>
                              {validation.valid ? '✓ Valid' : `✗ ${validation.error_count || 0} error${(validation.error_count || 0) !== 1 ? 's' : ''}`}
                            </span>
                          </td>
                          <td>
                            <span className="ov-check-count">
                              {checkCount} check{checkCount !== 1 ? 's' : ''}
                            </span>
                          </td>
                          <td>
                            <button
                              className="ov-see-details-btn"
                              onClick={() => setExpandedValidationDetailIndex(isExpanded ? null : actualIndex)}
                            >
                              {isExpanded ? 'Hide' : 'See Details'}
                            </button>
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr className="ov-validation-detail-row">
                            <td colSpan="4">
                              <div className="ov-validation-detail-panel">
                                <div className={`ov-detail-header ${validation.valid ? 'pass' : 'fail'}`}>
                                  {validation.valid ? '✓ All Checks Passed' : '✗ Validation Failed'}
                                </div>
                                <div className="ov-detail-checks">
                                  {Object.entries(validation.checks || {}).map(([checkName, checkResult]) => (
                                    <div key={checkName} className={`ov-detail-check ${!checkResult.passed ? 'failed' : ''}`}>
                                      <span className={`ov-detail-badge ${checkResult.passed ? 'pass' : 'fail'}`}>
                                        {checkResult.passed ? '✓' : '✗'}
                                      </span>
                                      <span className="ov-detail-name">{checkName.replace(/_/g, ' ')}</span>
                                      {!checkResult.passed && checkResult.errors && (
                                        <span className="ov-detail-err-count">
                                          {checkResult.errors.length} error{checkResult.errors.length !== 1 ? 's' : ''}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {Object.entries(validation.checks || {}).map(([checkName, checkResult]) =>
                                  checkResult.errors?.length > 0 ? (
                                    <div key={checkName} className="ov-detail-error-block">
                                      <div className="ov-detail-error-title">{checkName.replace(/_/g, ' ')}</div>
                                      {checkResult.errors.slice(0, 20).map((e, i) => (
                                        <div key={i} className="ov-detail-error-line">• {e}</div>
                                      ))}
                                      {checkResult.errors.length > 20 && (
                                        <div className="ov-detail-error-more">… and {checkResult.errors.length - 20} more</div>
                                      )}
                                    </div>
                                  ) : null
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            ) : !validationLoading && (
              <div className="ov-validation-empty">
                <p>No validation data available for this dataset.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default OutputViewer;
